import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
 
@Injectable(
  {providedIn: 'root' }//
)
export class IntersectionObserverService {
  private readonly preloadOffsetPx = 500; // Preload zone ahead of scroll
private readonly destructionOffset = 5;
private readonly observedQueue: Element[] = [];
  private readonly observers = new Map<Element, IntersectionObserver>();
  private readonly visibilitySubjects = new Map<Element,BehaviorSubject<boolean>>();
 
  /**
   * Observes the visibility of a DOM element using Intersection Observer API.
   * If already observed, returns immediately.
   *
   * @param element - The DOM element to observe.
   *
   * Creates a `BehaviorSubject` to track visibility state and sets up an `IntersectionObserver`.
   * Updates `BehaviorSubject` when visibility changes.
   *
   * Configured with a threshold of 0.25 (callback triggered when 25% visible).
   * Stores observer and `BehaviorSubject` in maps.
   */
  observeElement(element: Element) {
    if (this.observers.has(element)) {
      return;
    }
  
      const visibilitySubject = new BehaviorSubject<boolean>(false);
  this.visibilitySubjects.set(element, visibilitySubject);

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const isVisible = entry.isIntersecting;
      visibilitySubject.next(isVisible);

      if (isVisible) {
        // Track visibility only once
        if (!this.observedQueue.includes(entry.target)) {
          this.observedQueue.push(entry.target);

          // Destroy the oldest once buffer exceeds
          if (this.observedQueue.length > this.destructionOffset) {
            const oldElement = this.observedQueue.shift();
            if (oldElement) {
              this.unobserveElement(oldElement);
            }
          }
        }
      }
    });
  }, {
    threshold: 0.25,
    rootMargin: `0px 0px ${this.preloadOffsetPx}px 0px`, // Preload ahead
  });

  observer.observe(element);
  this.observers.set(element, observer);
  }
  
  /**
   * Returns an observable for visibility changes of the specified element.
   *
   * @param element - The DOM element to observe.
   * @returns An observable for visibility changes, or undefined if not observed.
   */
  getVisibilityObservable(element: Element) {
    return this.visibilitySubjects.get(element)?.asObservable();
  }
 
  /**
   * Unobserves the specified element by disconnecting and removing any associated IntersectionObserver,
   * completing and removing any associated visibility subject, and unsubscribing from any active subscription.
   *
   * @param element - The DOM element to unobserve.
   */
  unobserveElement(element: Element) {
    if (this.observers.has(element)) {
      this.observers.get(element)?.disconnect();
      this.observers.delete(element);
    }
  
    if (this.visibilitySubjects.has(element)) {
      this.visibilitySubjects.get(element)?.complete();
      this.visibilitySubjects.delete(element);
    }
  
    // Unsubscribe from any active subscription
    const subscription = (element as any)._observerSubscription;
    if (subscription) {
      subscription.unsubscribe();
    }
  }
  
 
 
  /**
   * Initializes an Intersection Observer for a given element and sets up a visibility change callback.
   *
   * @param element - The DOM element to observe.
   * @param updateCallback - Callback function invoked with visibility status.
   */
  initializeIntersectionObserver(element: Element, updateCallback: (isVisible: boolean) => void) {
    this.observeElement(element);
  
    // Unsubscribe from any existing subscription before adding a new one
    const existingSubscription = (element as any)._observerSubscription;
    if (existingSubscription) {
      existingSubscription.unsubscribe();
    }
  
    const visibility$ = this.getVisibilityObservable(element);
    if (visibility$) {
      const subscription = visibility$.subscribe(updateCallback);
      (element as any)._observerSubscription = subscription;
    }
  }
 
}
 